import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Volume2, VolumeX } from 'lucide-react';
import voiceService from '../utils/voiceService';

const WelcomeSplash = ({ onContinue, userName }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(voiceService.isEnabled);

  const handleTapToStart = () => {
    setIsPlaying(true);
    
    // Play welcome greeting
    voiceService.playWelcomeGreeting(userName, () => {
      setIsPlaying(false);
      // Small delay before continuing to app
      setTimeout(() => {
        onContinue();
      }, 500);
    });

    // If voice is disabled or already played, continue immediately after small delay
    if (!voiceService.isEnabled || voiceService.hasPlayedToday()) {
      setTimeout(() => {
        onContinue();
      }, 500);
    }
  };

  const toggleVoice = (e) => {
    e.stopPropagation();
    const newValue = !voiceEnabled;
    setVoiceEnabled(newValue);
    voiceService.setVoicePreference(newValue);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-black via-gray-900 to-black">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-lg">
        {/* Logo/Brand */}
        <div className="mb-8 animate-fade-in-up">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 mb-2">
            FokatMain
          </h1>
          <p className="text-gray-400 text-lg">Skill Gaming Platform</p>
        </div>

        {/* Welcome Message */}
        <div className="mb-8 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <div className="bg-gradient-to-r from-yellow-400/20 via-blue-400/20 to-yellow-400/20 border border-yellow-400/30 rounded-2xl p-6">
            {userName ? (
              <>
                <p className="text-yellow-400 text-xl font-semibold mb-2">
                  नमस्ते {userName}! 🙏
                </p>
                <p className="text-white text-lg">
                  Welcome back to FokatMain!
                </p>
              </>
            ) : (
              <>
                <p className="text-yellow-400 text-2xl font-semibold mb-2">
                  Welcome! 🎮
                </p>
                <p className="text-white text-lg">
                  Get ready to play & win exciting gadgets!
                </p>
              </>
            )}
          </div>
        </div>

        {/* Tap to Start Button */}
        <div className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
          <Button
            onClick={handleTapToStart}
            disabled={isPlaying}
            className="bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-black font-bold text-xl px-12 py-8 rounded-2xl transform transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-2xl"
          >
            {isPlaying ? (
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black"></div>
                <span>Playing Welcome...</span>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <span className="text-3xl">🎮</span>
                <span>Tap to Start</span>
              </div>
            )}
          </Button>
        </div>

        {/* Voice Toggle */}
        <div className="flex items-center justify-center gap-3 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
          <button
            onClick={toggleVoice}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-800/50 border border-gray-700 hover:border-yellow-400/50 transition-all"
          >
            {voiceEnabled ? (
              <>
                <Volume2 className="w-5 h-5 text-yellow-400" />
                <span className="text-gray-300 text-sm">Voice Greeting: ON</span>
              </>
            ) : (
              <>
                <VolumeX className="w-5 h-5 text-gray-500" />
                <span className="text-gray-500 text-sm">Voice Greeting: OFF</span>
              </>
            )}
          </button>
        </div>

        {/* Info Text */}
        <p className="text-gray-600 text-xs mt-6 animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
          {voiceService.hasPlayedToday() ? 
            "Welcome greeting played today" : 
            "Tap the button to hear your personalized welcome message"
          }
        </p>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-10 left-10 animate-bounce" style={{ animationDelay: '0s' }}>
        <div className="text-4xl">🏆</div>
      </div>
      <div className="absolute top-20 right-20 animate-bounce" style={{ animationDelay: '0.5s' }}>
        <div className="text-4xl">🎯</div>
      </div>
      <div className="absolute bottom-20 left-20 animate-bounce" style={{ animationDelay: '1s' }}>
        <div className="text-4xl">🎮</div>
      </div>
      <div className="absolute bottom-10 right-10 animate-bounce" style={{ animationDelay: '1.5s' }}>
        <div className="text-4xl">⚡</div>
      </div>

      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default WelcomeSplash;
